/* eslint-disable react/no-multi-comp */
import React from "react"
import { graphql } from "gatsby"
import {
  Header,
  Footer,
  GetInTouch,
  resolveComponent,
  MultiContentRenderer as ArticleUnits
} from "@components/sections"
import { Meta, GridLines, ContentRow, ContentItem, ContentAside, PageHeading } from "@shared"
import { CONSTANTS, URLS } from "@data"
import { logTool, css, featureFlag } from "@jsUtils"
import { getType, getFileLink } from "@jsUtils/contentful"

import Styles from "./article.module.scss"

const { THEMES, COLORS, PAGE_TRACKER_VALUES } = CONSTANTS
const log = logTool()

/**
 * Page Template
 * Render an Article page with this template
 * Note this shells out calls to resolveComponent to determine what
 * additional components to import
 * @param {object} props - react props
 */
const Article = props => {
  const { data } = props
  const { contentfulArticle, contentfulPageMeta } = data

  const { title, category, tags, date, author, content, download, source } = contentfulArticle
  const articleId = title.toLowerCase().replace(/\s/g, "-")

  const getArticleShortContent = () => {
    const articleContent = content?.[1]?.content?.childMarkdownRemark?.html
    if (articleContent) {
      return `${articleContent.replace(/<\/?[^>]+(>|$)/g, "").slice(0, 100)} ...`
    }
    return "Exo continues to be recognized for reshaping the digital health landscape with CB Insights recently naming the startup to its Digital Health 150 List of the most promising, private digital health companies in the world."
  }

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://www.exo.inc/news/${articleId}/`
    },
    headline: title,
    description: getArticleShortContent(),
    image:
      "https://images.ctfassets.net/ofxr6aa043ks/3K2RDxs047cf2TNyBsuuN7/67e1582e2f21d7c542d20980ff161a86/Digital-Heath-150-2021_export_V3-2.png",
    author: {
      "@type": "Organization",
      name: author
    },
    publisher: {
      "@type": "Organization",
      name: "",
      logo: {
        "@type": "ImageObject",
        url:
          "https://images.ctfassets.net/ofxr6aa043ks/LDeKdNjYx85CqZ2Pffv6t/700c8c3c0662cd65b0a562f790230ef7/exo_logo_2x.png"
      }
    },
    datePublished: date
  }

  log("Render article...", contentfulArticle)

  const downloadLink = getFileLink(download)
  // first row
  const leadContent = content?.slice(0, 1)?.[0]
  // all the rest
  const lastContent = content?.slice(1)

  const [ready, setReady] = React.useState(false)

  return (
    <>
      <Meta
        base={contentfulPageMeta}
        page={contentfulArticle}
        schemaMarkup={schemaMarkup}
        hubSpotObj={{
          contentTypeKey: category,
          contentTypeValue: PAGE_TRACKER_VALUES.BLOG_POST
        }}
      />
      <Header theme={THEMES.TIN} pageColor={COLORS.LIGHT} />
      <main role="main">
        <article className={Styles.articleWrapper}>
          {/* 01 header */}
          <ContentRow extraClass={css(Styles[`theme__${THEMES.TIN}`], Styles.articleBanner)}>
            <GridLines theme={THEMES.LIGHT} />
            <ContentAside extraClass={Styles.backLink} linkTo={URLS.NEWSROOM} />
            <PageHeading
              data={title}
              extraClass={Styles.articleHead}
              category={category}
              ready={ready}
              onReady={() => {
                setReady(true)
              }}
            />
          </ContentRow>

          {/* 02 first content row */}
          <ContentRow>
            <ContentAside
              {...{
                linkAuthor: !!featureFlag("link.authors"),
                extraClass: Styles.asideLabel,
                category,
                tags,
                date,
                author,
                source: source || "Exo",
                ...(!!downloadLink && { file: downloadLink })
              }}
            />
            {/* Article Body */}
            <ContentItem
              gridArea="article"
              extraClass={css(Styles.articleFirst, Styles[getType(leadContent)])}
            >
              {resolveComponent(leadContent)}
            </ContentItem>
          </ContentRow>

          <ArticleUnits data={lastContent} />
        </article>
      </main>
      <GetInTouch color={COLORS.FLURO} theme={THEMES.DARK} />
      <Footer />
    </>
  )
}

export default Article

export const pageQuery = graphql`
  query($slug: String!) {
    contentfulPageMeta(id: { eq: "423babd8-0278-5b2e-85f6-77fb7ee55bee" }) {
      ...GlobalMetaData
    }
    contentfulArticle(slug: { eq: $slug }) {
      category
      tags
      slug
      title
      shortTitle
      date
      source
      pageMeta {
        ...PageMetaData
      }
      download {
        ... on ContentfulAsset {
          file {
            url
          }
        }
      }
      content {
        ...MutliContentData
      }
    }
  }
`
